import { useToast } from '@zyxantech/tux'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { apiSlice } from '@/api/base'
import { QUERY_KEYS } from '@/constants/query-keys'
import { SEVERITY } from '@/constants/status'
import { realtimeClient } from '@/realtime/client'
import type { AppDispatch } from '@/store'
import { useAuthStore } from '@/store/auth.store'
import { useRealtimeStore } from '@/store/realtime.store'
import type { WsStatus } from '@/types/realtime'

export interface WebSocketHook {
  status: WsStatus
  subscribeToTag: (tagId: string) => void
  unsubscribeFromTag: (tagId: string) => void
}

const SEVERITY_TOAST = {
  [SEVERITY.CRITICAL]: 'error',
  [SEVERITY.HIGH]: 'error',
  [SEVERITY.MEDIUM]: 'warning',
  [SEVERITY.LOW]: 'info',
  [SEVERITY.INFO]: 'info',
} as const

/**
 * Owns the realtime connection lifecycle for the session: connect once
 * authenticated, disconnect on logout, and route alarm events to RTK cache
 * invalidation + a toast. Mounted once (in AppLayout). Components use the
 * returned subscribe/unsubscribe (or useTagSubscription) for tag values.
 */
export function useWebSocket(): WebSocketHook {
  const accessToken = useAuthStore((s) => s.accessToken)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const status = useRealtimeStore((s) => s.wsStatus)
  const dispatch = useDispatch<AppDispatch>()
  const toast = useToast()

  useEffect(() => {
    realtimeClient.setAlarmHandler((event) => {
      // A new alarm event invalidates the alarm list/badge so they refetch,
      // and surfaces a severity-coloured toast.
      dispatch(apiSlice.util.invalidateTags([{ type: QUERY_KEYS.ALARM, id: 'LIST' }]))
      toast.show(event.message, SEVERITY_TOAST[event.severity])
    })
    return () => {
      realtimeClient.setAlarmHandler(null)
    }
  }, [dispatch, toast])

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      realtimeClient.connect(accessToken)
    } else {
      realtimeClient.disconnect()
    }
  }, [isAuthenticated, accessToken])

  return {
    status,
    subscribeToTag: (tagId) => realtimeClient.subscribeToTag(tagId),
    unsubscribeFromTag: (tagId) => realtimeClient.unsubscribeFromTag(tagId),
  }
}

/**
 * Subscribe to a single tag's live values for the lifetime of a component.
 * Reference-counted by the client, so many cells can watch the same tag.
 */
export function useTagSubscription(tagId: string): void {
  useEffect(() => {
    realtimeClient.subscribeToTag(tagId)
    return () => {
      realtimeClient.unsubscribeFromTag(tagId)
    }
  }, [tagId])
}
